import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Recommendations from "@/components/Recommendations";
import { Input } from "@/components/ui/input";
import {
  BookOpenText, Newspaper, MagnifyingGlass, Gift,
  ChatCircleText, Users, ArrowRight, Star, Buildings,
  BookmarkSimple, Heart, BookOpen, Eye, CaretLeft, CaretRight, FireSimple, SignIn,
} from "@phosphor-icons/react";
import HomeCarousel from "@/components/HomeCarousel";

const MONTH_LABELS = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  return `há ${Math.floor(diff / 86400)}d`;
}

function fmtDatePT(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [recentManga, setRecentManga] = useState([]);
  const [popularManga, setPopularManga] = useState([]);
  const [recentNews, setRecentNews] = useState([]);
  const [releases, setReleases] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activeGiveaway, setActiveGiveaway] = useState(null);
  const [query, setQuery] = useState("");
  const [myCollection, setMyCollection] = useState([]);
  const [myCounts, setMyCounts] = useState({ collection: 0, wishlist: 0, read: 0 });
  const [featuredSeries, setFeaturedSeries] = useState([]);
  const [pubTab, setPubTab] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const [m, p, n, r, pub, d, act, gws] = await Promise.all([
          api.get("/manga"),
          api.get("/manga/popular", { params: { limit: 6 } }),
          api.get("/news"),
          api.get("/releases"),
          api.get("/publishers"),
          api.get("/stats/dashboard"),
          api.get("/community/activity", { params: { limit: 8 } }),
          api.get("/giveaways"),
        ]);
        const sortedRecent = [...m.data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 6);
        setRecentManga(sortedRecent);
        setPopularManga(p.data);
        setRecentNews(n.data.slice(0, 4));
        setReleases(r.data);
        setPublishers(pub.data.slice(0, 5));
        setDash(d.data);
        setActivity(act.data);
        // Find open giveaway (drawn_at in future) with most participants
        const openG = gws.data.filter(g => g.status === "open" && new Date(g.draw_at) > new Date());
        openG.sort((a, b) => (b.participant_count || 0) - (a.participant_count || 0));
        setActiveGiveaway(openG[0] || null);
      } catch (err) { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    api.get("/series", { params: { sort: "followers", limit: 6 } }).then(r => setFeaturedSeries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) { setMyCollection([]); setMyCounts({ collection: 0, wishlist: 0, read: 0 }); return; }    (async () => {
      try {
        const [c, w, r] = await Promise.all([api.get("/me/collection"), api.get("/me/wishlist"), api.get("/me/read")]);
        setMyCollection(c.data);
        setMyCounts({ collection: c.data.length, wishlist: w.data.length, read: r.data.length });
      } catch (e) { /* noop */ }
    })();
  }, [user]);

  const nextReleases = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return releases.filter(r => r.release_date >= today).sort((a, b) => a.release_date.localeCompare(b.release_date)).slice(0, 5);
  }, [releases]);

  const upcomingReleases = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const future = releases.filter(r => r.release_date >= today).sort((a, b) => a.release_date.localeCompare(b.release_date));
    if (future.length > 0) return future;
    // Fallback: most recent releases so the panel is never empty when data exists
    return [...releases].sort((a, b) => (b.release_date || "").localeCompare(a.release_date || ""));
  }, [releases]);

  const publisherTabs = useMemo(() => {
    const set = [];
    upcomingReleases.forEach(r => { if (r.publisher && !set.includes(r.publisher)) set.push(r.publisher); });
    return set.slice(0, 4);
  }, [upcomingReleases]);

  const filteredReleases = useMemo(() => {
    return (pubTab === "all" ? upcomingReleases : upcomingReleases.filter(r => r.publisher === pubTab)).slice(0, 6);
  }, [upcomingReleases, pubTab]);

  function handleSearch(e) {
    e.preventDefault();
    if (query.trim()) navigate(`/database?q=${encodeURIComponent(query.trim())}`);
  }

  const daysUntil = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso) - new Date();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  };

  return (
    <div data-testid="home-page" className="pt-20 max-w-7xl mx-auto px-4 lg:px-6 pb-16">
      {/* ============= SEARCH BAR ============= */}
      <form onSubmit={handleSearch} className="mb-6 relative max-w-2xl" data-testid="home-search-form">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          placeholder="Pesquisar manga, autores, editoras..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-11 h-11 bg-[#121217] border-white/10"
          data-testid="home-search-input"
        />
      </form>

      {/* ============= HERO: CAROUSEL + CTA/GIVEAWAY ============= */}
      <div className="grid lg:grid-cols-[1fr_360px] gap-4 mb-4">
        <HomeCarousel />
        {user ? <ActiveGiveawayCard g={activeGiveaway} /> : <JoinCommunityCard />}
      </div>

      {/* ============= STATS ROW ============= */}
      {user ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatBox icon={<BookmarkSimple size={22} weight="fill" />} label="NA COLEÇÃO" total={myCounts.collection} color="#E63946" to="/perfil/lista/collection" />
          <StatBox icon={<BookOpen size={22} weight="fill" />} label="JÁ LIDOS" total={myCounts.read} color="#10B981" to="/perfil/lista/read" />
          <StatBox icon={<Heart size={22} weight="fill" />} label="DESEJADOS" total={myCounts.wishlist} color="#F59E0B" to="/perfil/lista/wishlist" />
          <StatBox icon={<Star size={22} weight="fill" />} label="NO TOTAL" total={myCounts.collection + myCounts.wishlist + myCounts.read} color="#7209B7" to="/perfil" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatBox icon={<BookOpenText size={22} weight="fill" />} label="MANGÁS" total={dash?.manga.total} weekly={dash?.manga.weekly} color="#E63946" />
          <StatBox icon={<Newspaper size={22} weight="fill" />} label="NOTÍCIAS" total={dash?.news.total} weekly={dash?.news.weekly} color="#7209B7" />
          <StatBox icon={<ChatCircleText size={22} weight="fill" />} label="TÓPICOS" total={dash?.topics.total} weekly={dash?.topics.weekly} color="#F59E0B" />
          <StatBox icon={<Users size={22} weight="fill" />} label="MEMBROS" total={dash?.members.total} weekly={dash?.members.weekly} color="#10B981" />
        </div>
      )}

      {/* ============= MAIN GRID ============= */}
      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        {/* LEFT COLUMN */}
        <div className="space-y-6 min-w-0">
          {/* Destaques da Semana - carousel */}
          {popularManga.length > 0 && (
            <FeaturedCarousel items={popularManga} />
          )}

          {/* Recently Added */}
          <Section title="RECENTEMENTE ADICIONADOS" more={{ to: "/database", label: "Ver tudo" }}>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {recentManga.map(m => (
                <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="group" data-testid={`recent-${m.manga_id}`}>
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[#121217] border border-white/5">
                    <span className="absolute top-1.5 left-1.5 z-10 bg-[#E63946] text-white text-[9px] font-mono-accent font-bold px-1.5 py-0.5 rounded">NOVO</span>
                    {m.cover_url ? <img src={m.cover_url} alt={m.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" /> : <div className="w-full h-full grid place-items-center text-zinc-700 text-[10px]">SEM CAPA</div>}
                  </div>
                  <div className="mt-2 font-display font-semibold text-xs line-clamp-2 leading-tight">{m.title}</div>
                  {m.author && <div className="text-[10px] text-zinc-500 truncate">{m.author}</div>}
                </Link>
              ))}
            </div>
          </Section>

          {/* Next Releases */}
          {nextReleases.length > 0 && (
            <Section title="PRÓXIMOS LANÇAMENTOS" more={{ to: "/lancamentos", label: "Ver todos" }}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {nextReleases.map(r => {
                  const d = new Date(r.release_date);
                  return (
                    <Link key={r.release_id} to={`/lancamentos/${r.release_id}`} className="group" data-testid={`release-${r.release_id}`}>
                      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[#121217] border border-white/5">
                        <div className="absolute top-1.5 left-1.5 z-10 bg-[#F59E0B] text-black text-[9px] font-mono-accent font-black px-1.5 py-0.5 rounded leading-none">
                          <div>{String(d.getDate()).padStart(2, "0")}</div>
                          <div className="text-[7px]">{MONTH_LABELS[d.getMonth()]}</div>
                        </div>
                        {r.cover_url ? <img src={r.cover_url} alt={r.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" /> : <div className="w-full h-full grid place-items-center text-zinc-700 text-[10px]">SEM CAPA</div>}
                      </div>
                      <div className="mt-2 font-display font-semibold text-xs line-clamp-2 leading-tight">{r.title}</div>
                      {r.publisher && <div className="text-[10px] text-zinc-500 truncate">{r.publisher}</div>}
                    </Link>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Publishers */}
          {publishers.length > 0 && (
            <Section title="EDITORAS EM DESTAQUE" more={{ to: "/editoras", label: "Ver todas" }}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {publishers.map(p => (
                  <Link key={p.publisher_id} to={`/editoras/${p.publisher_id}`} className="bg-[#0f0f14] border border-white/5 rounded-xl p-3 hover:border-[#E63946]/40 transition-all text-center" data-testid={`publisher-${p.publisher_id}`}>
                    {p.image_url ? (
                      <img src={resolveImageUrl(p.image_url)} alt={p.name} className="w-full h-14 object-contain mb-2" />
                    ) : (
                      <div className="w-full h-14 grid place-items-center mb-2 text-zinc-500"><Buildings size={26} weight="duotone" /></div>
                    )}
                    <div className="font-display font-bold text-xs truncate">{p.name}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{p.manga_count || 0} obras</div>
                  </Link>
                ))}
              </div>
            </Section>
          )}

          {/* Community Activity */}
          {activity.length > 0 && (
            <Section title="ATIVIDADE DA COMUNIDADE" more={{ to: "/membros", label: "Ver tudo" }}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {activity.slice(0, 4).map((a, i) => (
                  <div key={i} className="bg-[#0f0f14] border border-white/5 rounded-xl p-3 flex items-start gap-2.5" data-testid={`activity-${i}`}>
                    {a.user_picture ? (
                      <img src={resolveImageUrl(a.user_picture)} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-xs font-bold flex-shrink-0">{(a.user_name || "?")[0]?.toUpperCase()}</div>
                    )}
                    <div className="flex-1 min-w-0 text-xs">
                      <Link to={`/membros/${a.user_slug || a.user_id}`} className="font-display font-semibold hover:text-[#E63946]">{a.user_name}</Link>
                      <div className="text-zinc-500 leading-snug line-clamp-2 mt-0.5">
                        {a.type === "rate" ? "avaliou" : {
                          collection: "adicionou à coleção", wishlist: "à wishlist", read: "marcou como lido",
                        }[a.action_key] || "atividade em"}{" "}
                        <Link to={`/manga/${a.manga_slug || a.manga_id}`} className="text-zinc-300 hover:text-white">{a.manga_title}</Link>
                      </div>
                      {a.type === "rate" && (
                        <div className="text-[#F59E0B] mt-0.5 flex items-center gap-0.5">
                          {[...Array(Math.min(5, Math.ceil((a.stars || 0) / 2)))].map((_, s) => <Star key={s} size={9} weight="fill" />)}
                        </div>
                      )}
                      <div className="text-[10px] text-zinc-600 mt-0.5">{timeAgo(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Recommendations */}
          {user && <Recommendations />}

          {/* Séries em Destaque */}
          {featuredSeries.length > 0 && (
            <section data-testid="featured-series">
              <div className="flex items-center justify-between mb-3 px-1">
                <h2 className="font-display font-bold text-lg flex items-center gap-2"><Star size={18} weight="fill" className="text-[#7209B7]" /> Séries em Destaque</h2>
                <Link to="/series" className="text-xs text-[#E63946] hover:underline font-display font-semibold flex items-center gap-1">Ver todas <ArrowRight size={12} weight="bold" /></Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {featuredSeries.map(s => (
                  <Link key={s.series_id} to={`/serie/${s.slug}`} className="group flex gap-3 bg-[#121217] border border-white/5 rounded-xl p-3 hover:border-[#7209B7]/40 transition-colors min-w-0" data-testid={`featured-series-${s.series_id}`}>
                    <div className="w-12 aspect-[2/3] rounded-md overflow-hidden bg-[#0f0f14] flex-shrink-0">
                      {s.image_url ? <img src={resolveImageUrl(s.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-zinc-700"><BookOpenText size={16} /></div>}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-sm leading-tight line-clamp-2 group-hover:text-[#7209B7] transition-colors">{s.name}</div>
                      <div className="text-[11px] text-zinc-500 mt-1">{s.volume_count} vols · {s.follower_count || 0} a seguir</div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6 min-w-0">
          {/* Most Viewed */}
          {popularManga.length > 0 && (
            <SidePanel title="MAIS VISTOS" more={{ to: "/database", label: "Ver tudo" }}>
              <div className="space-y-2">
                {popularManga.map((m, i) => (
                  <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-all" data-testid={`popular-${i}`}>
                    <div className="w-6 text-center font-display font-black text-lg text-zinc-500 flex-shrink-0">{i + 1}</div>
                    {m.cover_url ? (
                      <img src={m.cover_url} alt={m.title} className="w-10 h-14 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-14 bg-[#1C1C22] rounded flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-sm truncate">{m.title}</div>
                      {m.author && <div className="text-[10px] text-zinc-500 truncate">{m.author}</div>}
                    </div>
                  </Link>
                ))}
              </div>
            </SidePanel>
          )}

          {/* Recent News */}
          {recentNews.length > 0 && (
            <SidePanel title="NOTÍCIAS RECENTES" more={{ to: "/noticias", label: "Ver todas" }}>
              <div className="grid grid-cols-2 gap-2">
                {recentNews.slice(0, 4).map(n => (
                  <Link key={n.news_id} to={`/noticias/${n.news_id}`} className="group" data-testid={`news-${n.news_id}`}>
                    <div className="aspect-video rounded-lg overflow-hidden bg-[#1C1C22] mb-1.5">
                      {n.image_url ? <img src={n.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-zinc-700"><Newspaper size={22} /></div>}
                    </div>
                    <div className="font-mono-accent text-[9px] text-[#E63946]">{n.category || "NOTÍCIA"}</div>
                    <div className="font-display font-semibold text-xs line-clamp-2 leading-tight">{n.title}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">{fmtDatePT(n.created_at)}</div>
                  </Link>
                ))}
              </div>
            </SidePanel>
          )}

          {/* Releases by publisher */}
          {upcomingReleases.length > 0 && (
            <SidePanel title="LANÇAMENTOS POR EDITORA" more={{ to: "/lancamentos", label: "Ver calendário" }}>
              <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
                <button onClick={() => setPubTab("all")} data-testid="pubtab-all" className={`px-3 py-1.5 rounded-md text-[10px] font-display font-bold transition-all flex-shrink-0 ${pubTab === "all" ? "bg-[#E63946] text-white" : "bg-[#1C1C22] text-zinc-400 hover:text-white"}`}>Todos</button>
                {publisherTabs.map(p => (
                  <button key={p} onClick={() => setPubTab(p)} data-testid={`pubtab-${p}`} className={`px-3 py-1.5 rounded-md text-[10px] font-display font-bold transition-all flex-shrink-0 whitespace-nowrap ${pubTab === p ? "bg-[#E63946] text-white" : "bg-[#1C1C22] text-zinc-400 hover:text-white"}`}>{p}</button>
                ))}
              </div>
              <div className="space-y-1.5" data-testid="releases-publisher-list">
                {filteredReleases.map(r => {
                  const d = new Date(r.release_date);
                  return (
                    <Link key={r.release_id} to={`/lancamentos/${r.release_id}`} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-all text-xs" data-testid={`pub-release-${r.release_id}`}>
                      <div className="w-9 text-center flex-shrink-0">
                        <div className="font-display font-black text-sm text-[#E63946] leading-none">{String(d.getDate()).padStart(2, "0")}</div>
                        <div className="text-[8px] text-zinc-500 font-mono-accent">{MONTH_LABELS[d.getMonth()]}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-xs truncate">{r.title}</div>
                        {r.publisher && <div className="text-[10px] text-zinc-500 truncate">{r.publisher}</div>}
                      </div>
                    </Link>
                  );
                })}
                {filteredReleases.length === 0 && (
                  <div className="text-center py-4 text-[11px] text-zinc-500">Sem lançamentos</div>
                )}
              </div>
            </SidePanel>
          )}
        </div>
      </div>

      {/* ============= FOLLOW BANNER ============= */}
      <div className="mt-8 rounded-2xl overflow-hidden relative border border-[#E63946]/20" data-testid="follow-banner">
        <div className="absolute inset-0 bg-gradient-to-r from-[#E63946] via-[#c92f3c] to-[#7209B7] opacity-90" />
        <div className="relative px-6 py-7 flex items-center gap-5 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center flex-shrink-0">
            <BookOpenText size={26} weight="fill" className="text-white" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-black text-2xl text-white flex items-center gap-2">Segue o Portugal Manga <span aria-hidden>🇵🇹</span></div>
            <div className="text-sm text-white/85 mt-1">O maior catálogo de mangás em português de Portugal. Descobre, organiza e partilha a tua paixão!</div>
          </div>
          <div className="flex gap-5 flex-wrap">
            <BannerStat value={dash?.members?.total ?? 0} label="Membros" />
            <BannerStat value={dash?.manga?.total ?? 0} label="Mangás" />
            <BannerStat value={dash?.news?.total ?? 0} label="Notícias" />
            <BannerStat value={publishers.length} label="Editoras" />
          </div>
        </div>
      </div>
    </div>
  );
}

function BannerStat({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-display font-black text-2xl text-white">{value?.toLocaleString("pt-PT") ?? 0}</div>
      <div className="text-[10px] text-white/70 font-mono-accent uppercase">{label}</div>
    </div>
  );
}

/* ============= SUB-COMPONENTS ============= */

function StatBox({ icon, label, total, weekly, color, to }) {
  const inner = (
    <>
      <div className="w-11 h-11 rounded-lg grid place-items-center flex-shrink-0" style={{ backgroundColor: `${color}20`, color }}>
        {icon}
      </div>
      <div>
        <div className="font-mono-accent text-[10px] text-zinc-500 tracking-wider">{label}</div>
        <div className="font-display text-2xl font-extrabold leading-tight">{total?.toLocaleString("pt-PT") ?? "—"}</div>
        {weekly !== undefined && <div className="text-[10px] text-emerald-400 font-mono-accent mt-0.5">+{weekly} esta semana</div>}
      </div>
    </>
  );
  const cls = "bg-[#0f0f14] border border-white/5 rounded-xl p-4 flex items-center gap-3 transition-colors";
  if (to) return <Link to={to} className={`${cls} hover:border-white/15`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{inner}</Link>;
  return <div className={cls} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{inner}</div>;
}

function FeaturedCarousel({ items }) {
  const ref = useRef(null);
  function scroll(dir) {
    if (ref.current) ref.current.scrollBy({ left: dir * 340, behavior: "smooth" });
  }
  return (
    <section data-testid="featured-carousel">
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><FireSimple size={18} weight="fill" className="text-[#E63946]" /> Destaques da Semana</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => scroll(-1)} className="w-8 h-8 rounded-lg border border-white/10 grid place-items-center text-zinc-300 hover:bg-white/5" data-testid="featured-prev"><CaretLeft size={15} weight="bold" /></button>
          <button onClick={() => scroll(1)} className="w-8 h-8 rounded-lg border border-white/10 grid place-items-center text-zinc-300 hover:bg-white/5" data-testid="featured-next"><CaretRight size={15} weight="bold" /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
        {items.map((m, i) => (
          <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="group flex-shrink-0 w-[150px]" data-testid={`featured-${m.manga_id}`}>
            <div className="relative aspect-[2/3] rounded-xl overflow-hidden bg-[#121217] border border-white/5">
              <span className="absolute top-2 left-2 z-10 bg-[#E63946] text-white text-[9px] font-mono-accent font-bold px-1.5 py-0.5 rounded">{i < 2 ? "TOP" : "POPULAR"}</span>
              {m.cover_url ? <img src={m.cover_url} alt={m.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" /> : <div className="w-full h-full grid place-items-center text-zinc-700 text-[10px]">SEM CAPA</div>}
              <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1 bg-black/60 backdrop-blur px-1.5 py-0.5 rounded text-[10px] text-white"><Eye size={11} weight="fill" /> {m.view_count || 0}</div>
            </div>
            <div className="mt-2 font-display font-semibold text-sm line-clamp-2 leading-tight group-hover:text-[#E63946] transition-colors">{m.title}</div>
            {m.author && <div className="text-[11px] text-zinc-500 truncate">{m.author}</div>}
          </Link>
        ))}
      </div>
    </section>
  );
}

function JoinCommunityCard() {
  return (
    <div className="bg-gradient-to-br from-[#1a0810] via-[#0f0f14] to-[#180818] border border-[#E63946]/20 rounded-2xl p-6 flex flex-col items-center justify-center text-center" data-testid="join-community-card">
      <div className="w-14 h-14 rounded-2xl bg-[#E63946]/15 grid place-items-center mb-3">
        <Heart size={26} weight="fill" className="text-[#E63946]" />
      </div>
      <div className="font-display font-extrabold text-xl">Junta-te à comunidade!</div>
      <div className="text-sm text-zinc-400 mt-2 max-w-[260px]">Partilha a tua coleção, discute os teus mangás favoritos e fica a par de todas as novidades.</div>
      <Link to="/login" className="mt-5 btn-crimson h-11 px-6 rounded-lg grid place-items-center font-display font-bold text-sm w-full" data-testid="join-register-btn">
        <SignIn size={18} weight="bold" className="mr-2 inline" /> Entrar / Registar
      </Link>
    </div>
  );
}

function Section({ title, more, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="font-mono-accent text-[11px] text-zinc-500 tracking-widest">◢ {title}</h2>
        {more && <Link to={more.to} className="text-[11px] text-[#E63946] hover:underline font-display font-semibold flex items-center gap-1">{more.label} <ArrowRight size={11} weight="bold" /></Link>}
      </div>
      {children}
    </section>
  );
}

function SidePanel({ title, more, children }) {
  return (
    <div className="bg-[#0f0f14] border border-white/5 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold text-sm">{title}</div>
        {more && <Link to={more.to} className="text-[10px] text-[#E63946] hover:underline font-display font-semibold flex items-center gap-1">{more.label} <ArrowRight size={10} weight="bold" /></Link>}
      </div>
      {children}
    </div>
  );
}

function ActiveGiveawayCard({ g }) {
  if (!g) {
    return (
      <div className="bg-[#0f0f14] border border-white/5 rounded-2xl p-5 flex flex-col items-center justify-center text-center" data-testid="giveaway-empty">
        <Gift size={30} weight="duotone" className="text-zinc-600 mb-2" />
        <div className="font-display font-bold text-sm">Sem sorteios ativos</div>
        <div className="text-[11px] text-zinc-500 mt-1">Volta em breve!</div>
      </div>
    );
  }
  const days = Math.max(0, Math.ceil((new Date(g.draw_at) - new Date()) / (86400000)));
  return (
    <div className="bg-gradient-to-br from-[#1a0810] via-[#0f0f14] to-[#180818] border border-[#E63946]/20 rounded-2xl p-5 flex flex-col" data-testid="active-giveaway">
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono-accent font-bold tracking-wider bg-[#E63946] text-white">
          SORTEIO ATIVO
        </span>
        <span className="text-[10px] text-zinc-500 font-mono-accent">Termina em {days} {days === 1 ? "dia" : "dias"}</span>
      </div>
      <div className="flex items-start gap-3">
        {g.image_url ? (
          <img src={resolveImageUrl(g.image_url)} alt="" className="w-20 h-24 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="w-20 h-24 rounded-lg bg-[#1C1C22] grid place-items-center flex-shrink-0"><Gift size={26} weight="duotone" className="text-zinc-600" /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-extrabold text-base leading-tight line-clamp-2">{g.prize || g.title}</div>
          <div className="flex items-center gap-1 text-[11px] text-zinc-400 mt-2">
            <Users size={12} weight="bold" /> {g.participant_count || 0} participantes
          </div>
        </div>
      </div>
      <Link to={`/sorteio/${g.giveaway_id}`} className="mt-4 btn-crimson h-11 rounded-lg grid place-items-center font-display font-bold text-sm" data-testid="join-giveaway-btn">
        Participar no sorteio
      </Link>
      <Link to={`/sorteio/${g.giveaway_id}`} className="mt-2 text-center text-[11px] text-zinc-400 hover:text-white">
        Ver detalhes do sorteio →
      </Link>
    </div>
  );
}
